/** Console v0.5: Home, the Review inbox and the approval actions -- session A's routes (#375, contracts s.12).
 * Scope, the approval rules (a different person approves; only Lodge releases; the service team never approves) and
 * every refusal are the server's. `permissions` is advisory, so the screen can say why Approve is off. */

import { apiRequest } from "../api.js";

export type Service = "accounting" | "tax" | "assurance" | "financing";
export type InboxTab = "awaiting_you" | "exceptions" | "requested" | "done";

export interface ItemRow {
  item_id: string;
  organisation_id: string;
  organisation_name: string;
  service: Service;
  kind: string;
  agent: string;
  task: string;
  status: string; // slug
  status_label: string; // one of the six display statuses
  due: string | null; // ISO date
  updated_at: string;
}

export type ReleaseBlocked = "lodge_not_permitted" | "preparer_cannot_release" | "not_approved" | "service_team_forbidden";

export type ApproveBlocked = "capability_required" | "self_approval_not_allowed" | "not_awaiting_review" | "service_team_forbidden";

export interface ItemDetail extends ItemRow {
  summary: string;
  figures: Array<{ code: string; label: string; value_cents: number; delta_cents: number | null }>;
  why: string[];
  evidence: string[];
  sent_back_note: string | null;
  permissions: {
    can_approve: boolean;
    approve_blocked: ApproveBlocked | null;
    can_review: boolean;
    can_send_back: boolean;
    can_release: boolean;
    // Prabash's release rule (A): why Release is off, so the screen can say so (absent on older servers).
    release_blocked?: ReleaseBlocked | null;
  };
}

export interface Home {
  greeting_name: string | null;
  tiles: {
    awaiting_review: { count: number; due_today: number };
    exceptions: { count: number };
    lodgements_due_this_week: { count: number; next_due: string | null };
    team: { people: number; pending_invites: number } | null; // account admins only
  };
  queue: ItemRow[];
  month_end: { period: string; label: string; groups: Array<{ label: string; done: number; total: number }> };
  services: Array<{ service: Service; open: number; awaiting_you: number }>;
}

export interface ItemCounts {
  awaiting_you: number;
  exceptions: number;
  requested: number;
  done: number;
  open: number;
}

export function fetchHome(): Promise<Home> {
  return apiRequest("GET", "/api/home", "Preekie couldn't load Home.");
}

export function fetchItems(tab: InboxTab, filters: { organisation_id?: string; agent?: string } = {}): Promise<{ tab: InboxTab; items: ItemRow[]; counts: ItemCounts }> {
  const q = new URLSearchParams({ tab });
  if (filters.organisation_id) q.set("organisation_id", filters.organisation_id);
  if (filters.agent) q.set("agent", filters.agent);
  return apiRequest("GET", `/api/items?${q.toString()}`, "Preekie couldn't load the review inbox.");
}

export function fetchItem(itemId: string): Promise<{ item: ItemDetail }> {
  return apiRequest("GET", `/api/items/${encodeURIComponent(itemId)}`, "Preekie couldn't load this piece of work.");
}

export type ItemAction = "approve" | "review" | "send-back" | "release";

export function actOnItem(itemId: string, action: ItemAction, note?: string): Promise<{ item: ItemDetail }> {
  const fallback: Record<ItemAction, string> = {
    approve: "It wasn't approved.",
    review: "Your review wasn't recorded.",
    "send-back": "It wasn't sent back.",
    release: "It wasn't released.",
  };
  return apiRequest("POST", `/api/items/${encodeURIComponent(itemId)}/${action}`, fallback[action], action === "send-back" ? { note } : {});
}
